import React, { useState, useContext } from 'react';
import styles from './index.module.less';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { message } from 'antd';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UpcomingUpdateModal from '@/components/home/upcoming-update-modal';
import ExpiredExplainModal from '@/components/home/expired-explain-modal';
import {
    emptySrc,
    playIcon,
    alertAfterIcon,
    alertBeforeIcon,
    fetchCourseCurrentLearningResource,
    goToOtherPartner,
    goToSyllabus,
    getCourseCover,
} from './courses.config';
import { ICourseListItem, ITodayLiveListItem, LiveStatus, LearnStatus, WareStatus, IResourcesCoverListItem } from '@/types';
import { LiveIcon } from '@/svgs';
import { bindEvents } from '@/utils/sensors-data';
import SensorsDataContext from '@/components/home/context';

interface IProps {
    dataSource: ICourseListItem[];
    resourcesCover: IResourcesCoverListItem[];
    activeKey: number;
    collapsedIds: number[];
    handleClickTab: (activeKey: number, active: { key: number; name: string }) => void;
    handleCollapsed: (courseId: number) => void;
    handleDragEnd: (courseId: number, course: ICourseListItem) => void;
}

const otherPartnerMarkList = ['fengbian', 'kaijishicao'];

const Courses = (props: IProps) => {
    const { dataSource, resourcesCover, activeKey, handleClickTab, handleCollapsed, collapsedIds, handleDragEnd } = props;
    const [messageApi, contextHolder] = message.useMessage();

    const sensorsDataContextValues = useContext(SensorsDataContext);

    const [upcomingUpdateModalOpen, setUpcomingUpdateModalOpen] = useState(false);

    const [expiredExplainModalOpen, setExpiredExplainModalOpen] = useState(false);

    /* 跳转课程前的校验 */
    const beforeToCourse = (course: ICourseListItem) => {
        if (course.saasCourseType === 6 && course.saasCourseId === 0) {
            messageApi.open({
                type: 'error',
                content: '课程关联id配置错误，请联系学管重新配置哦~',
            });
            return false;
        }

        // 课程即将更新
        if (course.wareStatus === WareStatus.COURSE_UPCOMING_UPDATE) {
            setUpcomingUpdateModalOpen(true);
            return false;
        }

        return true;
    };

    const goToCourse = (course: ICourseListItem) => {
        const isCheck = beforeToCourse(course);
        if (!isCheck) return;

        // 第三方项目课程
        if (otherPartnerMarkList.includes(course.otherPartnerMark)) {
            goToOtherPartner(course);
            return;
        }

        // vcourseType === 4 g1课程
        if (course.vcourseType === 4) {
            window.open(`//live-hz.gaodun.com/gaodun/redirect?gaodunCourseID=${course.id}`);
            return;
        }

        window.open(course.currentStudyUrl, '_blank');
    };

    const handleClickCurrentStudy = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>, course: ICourseListItem) => {
        event.stopPropagation();

        bindEvents('pc_studytab_course_resource_click', {
            ...sensorsDataContextValues,
            resource_name: course.currentStudySubject,
            resource_id: course.currentStudyResourceId,
        });

        // 非saas课（包含第三方课程）
        if (course.vcourseType !== 6 || otherPartnerMarkList.includes(course.otherPartnerMark)) {
            goToCourse(course);
            return;
        }

        const isCheck = beforeToCourse(course);
        if (!isCheck) return;

        // saas课 去大纲
        if (!course.currentStudyResourceId) {
            goToSyllabus(course);
            return;
        }

        fetchCourseCurrentLearningResource(course);
    };

    const handleClickCourse = (course: ICourseListItem) => {
        bindEvents('pc_studytab_course_click', {
            ...sensorsDataContextValues,
            course_name: course.name,
            course_id: course.saasCourseId,
        });

        goToCourse(course);
    };

    const handleClickCourseNotice = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, course: ICourseListItem) => {
        event.stopPropagation();

        bindEvents('pc_studytab_course_notice_click', {
            ...sensorsDataContextValues,
            coursenotice_name: course.courseNotice.noticeTitle,
            course_id: course.saasCourseId,
        });

        if (course.courseNotice.noticeLink) {
            window.open(course.courseNotice.noticeLink, '_blank');
        }
    };

    const handleClickCourseTodayLive = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, live: ITodayLiveListItem) => {
        event.stopPropagation();

        bindEvents('pc_studytab_course_live_click', {
            ...sensorsDataContextValues,
            live_name: live.name,
            live_id: live.id,
        });

        window.open(live.ccLiveUrl, '_blank');
    };

    const makeTodayLive = (todayLive: ITodayLiveListItem[]) => {
        const live = todayLive.find(item => item.resourceStatus === LiveStatus.live);

        return (
            <div className={styles['course-today-live']}>
                {todayLive.length > 1 ? (
                    <div>
                        今日有<strong className={styles['course-today-live-number']}>{todayLive.length}</strong>场直播
                    </div>
                ) : (
                    <div>
                        今日直播
                        <span className={styles['course-today-live-time']}>
                            {dayjs(todayLive[0].startTime).format('HH:mm')}-{dayjs(todayLive[0].endTime).format('HH:mm')}
                        </span>
                    </div>
                )}
                {live && (
                    <div className={styles['course-today-live-btn']} onClick={event => handleClickCourseTodayLive(event, live)}>
                        正在直播中 <LiveIcon />
                    </div>
                )}
            </div>
        );
    };

    const makeExpiredCourse = (course: ICourseListItem) => {
        return (
            <div className={classNames(styles['course-card'], styles['expired-course-card'])}>
                <div
                    className={styles['course-card-head']}
                    onClick={() => {
                        messageApi.open({
                            type: 'warning',
                            content: '课程已到期',
                        });
                    }}
                >
                    <div>
                        <div className={styles['course-learn-status']}>已到期</div>
                        <div className={classNames(styles['course-name'], styles['expired-course-name'])}>{course.name}</div>
                    </div>
                </div>
                {course.courseExpireTime && <div className={styles['course-expired-time']}>到期时间：{course.courseExpireTime}</div>}
            </div>
        );
    };

    const makeMyCourse = (course: ICourseListItem) => {
        if (activeKey === 2) {
            return makeExpiredCourse(course);
        }

        const coverData = getCourseCover(resourcesCover, course);
        return (
            <div className={styles['course-card']}>
                <div className={styles['course-card-head']} onClick={() => handleClickCourse(course)}>
                    <div>
                        <div
                            className={classNames(styles['course-learn-status'], {
                                [styles['learn-status-ready-for-study']]: course.learnStatus === LearnStatus.READY_FOR_STUDY,
                            })}
                        >
                            {course.learnStatusDesc}
                        </div>
                        <div className={styles['course-name']}>{course.name}</div>
                        {course.isAuditionCourse === 1 && <div className={styles['course-audition']}>试学</div>}
                    </div>
                    <div className={styles['course-detail-btn']}>
                        <span>课程详情</span>
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 9L4.64645 5.35355C4.84171 5.15829 4.84171 4.84171 4.64645 4.64645L1 1"
                                stroke="#FF6E00"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                </div>
                <div className={styles['course-card-content']} onClick={event => handleClickCurrentStudy(event, course)}>
                    <div
                        className={classNames(styles['course-current-study'], {
                            [styles['is-border']]: !coverData.isDefault,
                        })}
                    >
                        <img className={styles['course-current-study-cover']} src={coverData.src} />
                        {course.currentStudyResourceMark === 'video' && <img src={playIcon} className={styles.playIcon}></img>}
                    </div>
                    <div className={styles['course-card-content-rt']}>
                        {!!course.currentStudySubject && (
                            <div className={styles['course-current-study-title']} onClick={event => handleClickCurrentStudy(event, course)}>
                                上次学到：{course.currentStudySubject}
                            </div>
                        )}
                        {!!course.courseExpireTime && (
                            <div className={styles['course-valid-time']}>有效期至：{course.courseExpireTime}</div>
                        )}
                    </div>
                </div>
                {!!course.lives && makeTodayLive(course.lives)}
                {course.courseNotice && (
                    <div
                        className={classNames(styles['course-alert'], {
                            [styles['pointer']]: !!course.courseNotice.noticeLink,
                        })}
                        onClick={event => handleClickCourseNotice(event, course)}
                    >
                        <img className={styles['course-alert-before-icon']} src={alertBeforeIcon} alt="" />
                        {course.courseNotice.noticeDesc}
                        {course.courseNotice.noticeLink && (
                            <img className={styles['course-alert-after-icon']} src={alertAfterIcon} alt="" />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const SortableItem = (props: { id: string; children: React.ReactNode; disabled: boolean; className: string }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id, disabled: props.disabled });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };

        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={props.className}>
                {props.children}
            </div>
        );
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // 拖动激活的最小距离
            },
        })
    );

    const onDragEnd = (event: DragEndEvent, course: ICourseListItem) => {
        if (activeKey === 2) return;

        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = course.children.findIndex(child => child.id === active.id);
            const newIndex = course.children.findIndex(child => child.id === over?.id);
            const newChildren = arrayMove(course.children, oldIndex, newIndex);
            handleDragEnd(course.id, { ...course, children: newChildren });
        }
    };

    const makeCoursePackage = (course: ICourseListItem) => {
        return (
            <>
                <div
                    className={classNames(styles['course-list-item-package-head'], {
                        [styles['is-collapsed']]: collapsedIds.includes(course.id),
                    })}
                    onClick={() => {
                        handleCollapsed(course.id);
                    }}
                >
                    <div
                        className={classNames(styles['course-list-item-package-expand-icon'], {
                            [styles['is-collapsed']]: collapsedIds.includes(course.id),
                        })}
                    ></div>
                    <div className={styles['course-list-item-package-head-title']}>{course.name}</div>
                </div>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event: DragEndEvent) => onDragEnd(event, course)}
                >
                    <div
                        className={styles['course-list-item-package-content']}
                        style={{ display: collapsedIds.includes(course.id) ? 'none' : 'block' }}
                    >
                        <SortableContext items={course.children.map(child => child.id)} strategy={verticalListSortingStrategy}>
                            {course.children.map((child: any) => (
                                <SortableItem
                                    key={child.id}
                                    id={child.id}
                                    disabled={activeKey === 2}
                                    className={styles['course-list-item-package-list-item']}
                                >
                                    {makeMyCourse(child)}
                                </SortableItem>
                            ))}
                        </SortableContext>
                    </div>
                </DndContext>
            </>
        );
    };

    return (
        <>
            {contextHolder}
            <div>
                <div className={styles['course-tabs']}>
                    <div
                        className={classNames(styles['course-tabs-tab'], {
                            [styles['course-tabs-tab-active']]: activeKey === 0,
                        })}
                        onClick={() => handleClickTab(0, { key: 0, name: '我的课程' })}
                    >
                        我的课程
                    </div>
                    <div
                        className={classNames(styles['course-tabs-tab'], {
                            [styles['course-tabs-tab-active']]: activeKey === 2,
                        })}
                        onClick={() => handleClickTab(2, { key: 0, name: '到期课程' })}
                    >
                        到期课程
                    </div>
                </div>
                <div className="course-list">
                    {!!dataSource.length &&
                        dataSource.map(course => (
                            <div
                                className={classNames({
                                    [styles['course-list-item']]: !course.isBig,
                                    [styles['course-list-item-package']]: course.isBig,
                                })}
                                key={course.id}
                            >
                                {course.isBig ? makeCoursePackage(course) : makeMyCourse(course)}
                            </div>
                        ))}
                    {!dataSource.length && (
                        <div className={styles['course-list-empty']}>
                            <img src={emptySrc} alt="" />
                        </div>
                    )}
                </div>
                {activeKey === 2 && (
                    <div className={styles['course-expired-tips']}>
                        没找到到期课程？<a onClick={() => setExpiredExplainModalOpen(true)}>查看原因</a>
                    </div>
                )}
                {upcomingUpdateModalOpen && <UpcomingUpdateModal handleClose={() => setUpcomingUpdateModalOpen(false)} />}
                {expiredExplainModalOpen && <ExpiredExplainModal handleClose={() => setExpiredExplainModalOpen(false)} />}
            </div>
        </>
    );
};

export default Courses;
